import type { ReactNode } from 'react';
import { ICP_MUST_RATING, ICP_RATING_LABELS, icpReadingEstimate, type IcpItem, type IcpRating } from '@nest-recruit/shared';
import { estimateCostOf, jevLineCount } from '../../../icp/icp-form';
import { useProfileLines } from './profile-lines-store';
import styles from './builder.module.css';

// JOB-13's right column at 1280px and wider for a job ranked by the ideal candidate (ADR 0062 §4, mockup M1): what the ranking
// will look for, read from the lines on screen, so it follows each dot as it is clicked.

const JEV_ONLY_RATINGS: readonly IcpRating[] = [4, 3, 2, 1];
const COST_SAMPLE_CVS = 100;

function Group({ title, items }: { readonly title: string; readonly items: readonly IcpItem[] }): ReactNode {
  if (items.length === 0) return null;
  return (
    <section className={styles.summaryGroup}>
      <h3 className={styles.summaryGroupTitle}>{title}</h3>
      <ul className={styles.summaryList}>
        {items.map((item) => (
          <li key={item.id} className={styles.summaryItem}>
            <span className={styles.summaryLabel}>
              {item.text}
              <span className={styles.summaryWeight}>{item.rating}</span>
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

export function ProfileSummary(): ReactNode {
  const items = useProfileLines() ?? [];
  const musts = items.filter((item) => item.rating === ICP_MUST_RATING);
  const facts = items.filter((item) => item.kind === 'fact' && item.rating !== ICP_MUST_RATING);
  const jevLines = jevLineCount(items);
  const cost = jevLines > 0 ? estimateCostOf(icpReadingEstimate(items, null, COST_SAMPLE_CVS).label) : null;
  return (
    <aside className={styles.summary} aria-labelledby="profile-summary-title">
      <h2 id="profile-summary-title" className={styles.summaryTitle}>
        What the ranking will look for
      </h2>
      {musts.length > 0 ? (
        <Group title="Must show" items={musts} />
      ) : (
        <p className={styles.help}>
          <b>Nothing is a must-have yet.</b> Rate at least one line {ICP_MUST_RATING} to open the job.
        </p>
      )}
      {JEV_ONLY_RATINGS.map((rating) => (
        <Group key={rating} title={ICP_RATING_LABELS[rating]} items={items.filter((item) => item.kind !== 'fact' && item.rating === rating)} />
      ))}
      <Group title="Facts · worked out for free" items={facts} />
      {items.length > 0 && (
        <p className={styles.help}>
          Jev reads <b>{jevLines} {jevLines === 1 ? 'line' : 'lines'}</b> · facts are free{cost !== null && (
            <>
              {' '}· <b>{cost}</b> per {COST_SAMPLE_CVS} CVs
            </>
          )}
        </p>
      )}
      {musts.length > 0 && <p className={styles.help}>People missing a must-have stay below everyone who shows every must-have.</p>}
    </aside>
  );
}
